import {
  balanceLine,
  canAfford,
  chosenByLine,
  costOf,
  creditsLabel,
  lastSungLine,
  parsePassages,
  SERMON_FILE_ACCEPT,
  SERMON_FILE_HINT,
  sermonFileProblem,
  sermonReadActions,
  sermonUploadType,
  shortLine,
  type AiActionKey,
  type FlowSeverity,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAccess, useServiceAccess } from '../access/useAccess.ts'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import { sermonFileGroups, usePlanFiles, useUploadPlanFile, type PlanFile } from './usePlanFiles.ts'
import { servicesKeys, usePlan } from './useServices.ts'

/**
 * AI song suggestions from the sermon (docs/15-MUSIC-AND-AI.md): a plan's
 * sermon card and its edit panel, the Suggestions page (from the library, to
 * discover, the set's flow). Nothing here applies a suggestion by itself:
 * every one waits for a person. (Song themes are automatic: useSongThemes.)
 *
 * Keys live under the plan (`servicesKeys.plan`) and the song, so anything
 * that refreshes a plan — a song added, a key changed — refreshes its
 * suggestions and their flow too. While the worker reads the sermon or
 * makes the suggestions, the queries ask again every few seconds.
 */
export const aiKeys = {
  status: () => [...servicesKeys.all(), 'ai'] as const,
  sermon: (planId: string) => [...servicesKeys.plan(planId), 'sermon'] as const,
  suggestions: (planId: string) => [...servicesKeys.plan(planId), 'suggestions'] as const,
}

const POLL_MS = 3000

/** Is AI set up here, this month's credits, and what each action costs. */
export function useAiStatus() {
  const api = useApi()
  const enabled = useAccess().inServices
  return useQuery({
    queryKey: aiKeys.status(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.ai.get()
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
}

/**
 * What an AI action costs, for its button (docs/15, "AI credits"):
 * "Regenerate · 1 credit", what is left, and — when the church is short
 * or the action is switched off — why it can't run. Cosmetic: the server
 * charges and refuses.
 */
export function useAiCost(actions: readonly AiActionKey[]) {
  const status = useAiStatus().data
  const credits = status?.enabled ? status.credits : null
  if (!credits)
    return {
      shown: false,
      credits: 0,
      cost: '',
      label: (verb: string) => verb,
      ready: false,
      reason: null,
      left: null,
    } as const
  const cost = costOf(credits.actions, actions)
  const affordable = canAfford(credits, cost.credits)
  return {
    shown: true,
    credits: cost.credits,
    /** "1 credit". */
    cost: creditsLabel(cost.credits),
    /** "Regenerate · 1 credit". */
    label: (verb: string) => `${verb} · ${creditsLabel(cost.credits)}`,
    /** It can run now. */
    ready: cost.enabled && affordable,
    /** Why it can't, in one line; null when it can. */
    reason: !cost.enabled
      ? 'Switched off for now by ForHim.'
      : affordable
        ? null
        : shortLine(cost.credits, credits),
    /** "37 of 50 left this month". */
    left: balanceLine(credits),
  }
}

export type AiCost = ReturnType<typeof useAiCost>

// ---- the sermon ----------------------------------------------------------------------------------

export function usePlanSermon(planId: string) {
  const api = useApi()
  return useQuery({
    queryKey: aiKeys.sermon(planId),
    enabled: planId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).sermon.get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) => {
      const sermon = query.state.data?.sermon
      return sermon?.state === 'processing' || sermon?.suggestions.state === 'processing'
        ? POLL_MS
        : false
    },
  })
}

export type PlanSermonView = NonNullable<
  NonNullable<ReturnType<typeof usePlanSermon>['data']>['sermon']
>

/**
 * The plan's Sermon card: whether it shows at all (AI set up), what it
 * says, and what the viewer may do — Editors on the plan's service type
 * change it and open the suggestions.
 */
export function useSermonCard(plan: { id: string; serviceTypeId: string }) {
  const sermon = usePlanSermon(plan.id)
  const access = useServiceAccess()
  const editable = access.can(plan.serviceTypeId, 'editor')
  const view = sermon.data?.sermon ?? null
  const extracted = view?.state === 'ready' ? view.extracted : null
  // Reading it again: its suggestions, and a recording's transcription first.
  const readCost = useAiCost(sermonReadActions(view?.file?.read ? null : (view?.file ?? null)))
  return {
    query: sermon,
    /** AI is set up here: the card shows. */
    shown: sermon.data?.enabled === true,
    sermon: view,
    /** The sermon's title, else its file's (a sermon chosen as a file has no title yet). */
    heading: view?.title ?? view?.file?.title ?? 'Untitled sermon',
    extracted,
    editable,
    /** The Suggestions page is its Editors'. */
    canSuggest: editable,
    /** One line under the title while something is happening, or why it failed. */
    status:
      view?.state === 'processing'
        ? 'Reading the sermon…'
        : view?.state === 'failed'
          ? (view.error ?? 'The sermon couldn’t be read.')
          : view?.suggestions.state === 'processing'
            ? 'Choosing songs…'
            : null,
    failed: view?.state === 'failed',
    // A draft from a series outline has a title and passage, not yet read: not empty.
    empty: !view || (view.state === 'empty' && !view.title && view.passages.length === 0),
    /** What to say about the file it was read from, beyond its name. */
    fileNote: view?.file?.removed
      ? 'Removed from the plan. What was read from it stays.'
      : view?.shortened
        ? 'Long: only its first 60,000 characters were read.'
        : null,
    /** What Read again costs, and whether it can run. */
    readCost,
  }
}

export function useSermonEditor(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
    return queryClient.invalidateQueries({ queryKey: servicesKeys.plan(planId) })
  }
  const save = useMutation({
    mutationFn: async (body: {
      title?: string | null
      passages?: string[]
      notes?: string | null
      sourceFileId?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).sermon.put(body)
      if (error) throw error
      return data
    },
    onSuccess: (sermon) => {
      queryClient.setQueryData(aiKeys.sermon(planId), { enabled: true, sermon })
      void refresh()
    },
  })
  const reread = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).sermon.read.post()
      if (error) throw error
      return data
    },
    onSuccess: () => void refresh(),
    onError: (error) => notifyFailure(error, () => reread.mutate(), 'Couldn’t read it again'),
  })
  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.services.plans({ id: planId }).sermon.delete()
      if (error) throw error
    },
    onSuccess: () => {
      notify({ title: 'Removed the sermon', description: 'Its suggestions went with it.' })
      void refresh()
    },
  })
  return { save, reread, remove }
}

/** What a sermon file is called among the plan's files. */
const fileTitle = (name: string) => `Sermon – ${name.replace(/\.[a-z0-9]+$/i, '')}`

/** A browser leaves a Markdown file's type empty (some an old .doc's): the name says what it is. */
const typeOf = (file: File) => sermonUploadType(file.type, file.name)

/**
 * The sermon's edit panel: its fields, a file to upload (through the plan's
 * Files, the plan-files flow) or the one it has, and Save — which uploads
 * first, then saves, so the sermon is read once.
 */
export function useSermonForm(planId: string, sermon: PlanSermonView | null, open: boolean) {
  const editor = useSermonEditor(planId)
  const upload = useUploadPlanFile({ planId })
  const [title, setTitle] = useState('')
  const [passages, setPassages] = useState('')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [picked, setPicked] = useState<PlanFile | null>(null)
  const [keepFile, setKeepFile] = useState(true)
  // A fresh form each time it opens, from the sermon as it is now.
  const [openedFor, setOpenedFor] = useState(false)
  if (open !== openedFor) {
    setOpenedFor(open)
    if (open) {
      setTitle(sermon?.title ?? '')
      setPassages(sermon?.passages.join('; ') ?? '')
      setNotes(sermon?.notes ?? '')
      setFile(null)
      setPicked(null)
      setKeepFile(true)
    }
  }
  const fileProblem = file
    ? sermonFileProblem({ contentType: typeOf(file), fileName: file.name, sizeBytes: file.size })
    : null
  const pending = editor.save.isPending || upload.isPending
  // Saving reads it: what that costs, with the file it will have.
  // A kept file read before (a recording transcribed) is not transcribed again.
  const keptFile = keepFile && !file && !sermon?.file?.read ? (sermon?.file ?? null) : null
  const readCost = useAiCost(
    sermonReadActions(
      file ? { contentType: typeOf(file), fileName: file.name } : (picked ?? keptFile),
    ),
  )

  async function submit() {
    let sourceFileId = picked?.id ?? (keepFile ? (sermon?.file?.id ?? null) : null)
    if (file) {
      if (fileProblem) throw new Error(fileProblem)
      const uploaded = await upload.mutateAsync({
        title: fileTitle(file.name),
        file,
        fileName: file.name,
        contentType: typeOf(file),
      })
      sourceFileId = uploaded.id
    }
    return editor.save.mutateAsync({
      title: title.trim() || null,
      passages: parsePassages(passages),
      notes: notes.trim() || null,
      sourceFileId,
    })
  }

  return {
    title,
    setTitle,
    passages,
    setPassages,
    notes,
    setNotes,
    /** A file chosen to upload. */
    file,
    chooseFile: (next: File | null) => {
      setFile(next)
      if (next) {
        setPicked(null)
        setKeepFile(false)
      }
    },
    /** One already on the plan, chosen to read instead (by reference: no upload). */
    pickFile: (next: PlanFile) => {
      setPicked(next)
      setFile(null)
      setKeepFile(false)
    },
    /** The file the sermon will have: one picked from the plan, else the one it has now. */
    currentFile: file
      ? null
      : picked
        ? {
            id: picked.id,
            title: picked.title,
            fileName: picked.fileName,
            removed: false,
            read: false,
          }
        : keepFile
          ? (sermon?.file ?? null)
          : null,
    dropFile: () => {
      setFile(null)
      setPicked(null)
      setKeepFile(false)
    },
    /** What the upload offers, and its hint. */
    accept: SERMON_FILE_ACCEPT,
    hint: SERMON_FILE_HINT,
    fileProblem,
    pending,
    /** What reading it costs once saved (it is read only when it changed). */
    readCost,
    error: upload.error ?? editor.save.error,
    submit,
    remove: editor.remove,
  }
}

/**
 * "Use a file from this plan": the plan's files a sermon can be read from
 * (`sermonFileGroups`: the sermon item's first), and which is the sermon's now.
 */
export function useSermonFileChoices(planId: string, currentFileId: string | null) {
  const files = usePlanFiles(planId)
  const plan = usePlan(planId)
  const groups = sermonFileGroups(
    files.data ?? [],
    (plan.data?.items ?? []).map((item) => item.id),
  )
  return {
    query: files,
    groups,
    currentFileId,
    /** Nothing on the plan a sermon can be read from. */
    empty: files.isSuccess && groups.length === 0,
  }
}

/**
 * Choosing a file from the plan, from the Sermon card: it becomes the
 * sermon's file (by reference), and is read. The one it has already: nothing to do.
 */
export function useChooseSermonFile(planId: string, currentFileId: string | null) {
  const editor = useSermonEditor(planId)
  const mutation = useMutation({
    mutationFn: (file: PlanFile) => editor.save.mutateAsync({ sourceFileId: file.id }),
    onSuccess: (_, file) =>
      notify({
        title: `Reading “${file.fileName ?? file.title}”`,
        description: 'Songs are suggested once it is read.',
      }),
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t use that file'),
  })
  return {
    /** The file being saved as the sermon's, while it is. */
    pendingId: mutation.isPending ? (mutation.variables?.id ?? null) : null,
    choose: (file: PlanFile, done: () => void) => {
      if (file.id === currentFileId) done()
      else mutation.mutate(file, { onSuccess: done })
    },
  }
}

// ---- suggestions -----------------------------------------------------------------------------------

export function usePlanSuggestions(planId: string, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: aiKeys.suggestions(planId),
    enabled: planId !== '' && options.enabled !== false,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).suggestions.get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) => {
      const data = query.state.data
      return data?.state === 'processing' || data?.sermon?.state === 'processing' ? POLL_MS : false
    },
  })
}

export type PlanSuggestions = NonNullable<ReturnType<typeof usePlanSuggestions>['data']>
export type LibrarySuggestion = PlanSuggestions['library'][number]
export type DiscoverSuggestion = PlanSuggestions['discover'][number]
export type FlowHint = PlanSuggestions['flow'][number]

/** A flow hint's tone: how it is shown (calm, worth a look, worth a rethink). */
export const flowTone = (severity: FlowSeverity) =>
  severity === 'caution' ? 'warning' : severity === 'fine' ? 'brand' : 'success'

/**
 * The Suggestions page: what it shows, why it is empty, and what the
 * viewer may do — its plan's Editors add library songs to the order and
 * regenerate; Songs Editors add discoveries to the library.
 */
export function useSuggestionsPage(plan: { id: string; serviceTypeId: string } | undefined) {
  const access = useServiceAccess()
  const songsEditor = useAccess().hasSomewhere('songs', 'editor')
  const editable = plan ? access.can(plan.serviceTypeId, 'editor') : false
  const query = usePlanSuggestions(plan?.id ?? '', { enabled: editable })
  const data = query.data
  const actions = useSuggestionActions(plan?.id ?? '')
  const sermon = data?.sermon ?? null
  const working = data?.state === 'processing' || sermon?.state === 'processing'
  const empty = !sermon || sermon.state === 'empty'
  const regenerateCost = useAiCost(['plan_suggestions_regenerate'])
  return {
    query,
    data,
    editable,
    songsEditor,
    sermon,
    extracted: sermon?.extracted ?? null,
    working,
    /** Why there is nothing to show, in words; null when there is. */
    emptyReason: !data
      ? null
      : !data.enabled
        ? 'AI suggestions aren’t set up on this server yet.'
        : empty
          ? 'Add the sermon to get suggestions: a title and passages are enough to start.'
          : sermon.state === 'processing'
            ? 'Reading the sermon…'
            : sermon.state === 'failed'
              ? 'The sermon couldn’t be read. Open the plan and read it again.'
              : data.state === 'processing'
                ? 'Choosing songs for this sermon…'
                : data.state === 'failed'
                  ? (data.error ?? 'Songs couldn’t be chosen. Try Regenerate.')
                  : data.library.length === 0 && data.discover.length === 0
                    ? 'Nothing to suggest right now. Regenerate for another look.'
                    : null,
    lastSung: (song: LibrarySuggestion) => lastSungLine(song.lastSungOn),
    /** "Chosen by 42 churches for Resurrection": other churches' choice for the sermon's themes. */
    chosenBy: (song: LibrarySuggestion | DiscoverSuggestion) => chosenByLine(song.chosenBy),
    canRegenerate: editable && !working && sermon?.state === 'ready' && regenerateCost.ready,
    /** What Regenerate costs ("Regenerate · 1 credit"), and why it can't run. */
    regenerateCost,
    ...actions,
  }
}

export function useSuggestionActions(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
    return queryClient.invalidateQueries({ queryKey: servicesKeys.plan(planId) })
  }
  const suggestion = (id: string) =>
    api.api.v1.services.plans({ id: planId }).suggestions({ suggestionId: id })

  const regenerate = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .suggestions.regenerate.post()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      notify({ title: 'Choosing songs again', description: 'Dismissed songs stay dismissed.' })
      void refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t regenerate'),
  })
  const dismiss = useMutation({
    mutationFn: async (input: { id: string; title: string }) => {
      const { error } = await suggestion(input.id).dismiss.post()
      if (error) throw error
    },
    onSuccess: (_, input) => {
      notify({
        title: `Dismissed “${input.title}”`,
        description: 'Not suggested again for this plan.',
      })
      void refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t dismiss it'),
  })
  const addToOrder = useMutation({
    mutationFn: async (input: { id: string; title: string }) => {
      const { data, error } = await suggestion(input.id).order.post()
      if (error) throw error
      return data
    },
    onSuccess: (_, input) => {
      notify({
        tone: 'success',
        title: `Added “${input.title}” to the order`,
        description: 'After the last song.',
      })
      void refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t add it'),
  })
  const addToLibrary = useMutation({
    mutationFn: async (input: { id: string; title: string; addToOrder: boolean }) => {
      const { data, error } = await suggestion(input.id).library.post({
        addToOrder: input.addToOrder,
      })
      if (error) throw error
      return data
    },
    onSuccess: (_, input) => {
      notify({
        tone: 'success',
        title: input.addToOrder
          ? `Added “${input.title}” to the library and the order`
          : `Added “${input.title}” to the library`,
        description: 'With its Apple Music recording.',
      })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.all() })
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t add it to the library'),
  })
  return { regenerate, dismiss, addToOrder, addToLibrary }
}
